import { getRandomInteger } from "./helpers";

import type { I18nTranslationFunction } from "~/components/i18n/context";
import type {
  QuizViewQuestion,
  QuizViewQuestionValidityState,
} from "~/components/quizView/QuizViewManager";
import type {
  ClassroomQuizQuestion,
  FillInTheBlankQuestion,
  MatchingQuestion,
  QuizQuestionItem,
} from "~/graphql/types";

/**
 * Determines if a user's answer to a quiz question is
 * correct.
 */
export function check(
  expected: string | string[] = [],
  actual: string | string[] = [],
  options?: { order: boolean },
): QuizViewQuestionValidityState {
  const _expected: string[] = new Array(expected).flat();
  const _actual: string[] = new Array(actual).flat();

  if (options?.order) {
    const invalid = _expected.filter((a, i) => _actual?.[i] !== a);
    const valid = _expected.filter((a, i) => _actual?.[i] === a);

    return {
      valid: !invalid.length && valid.length === _expected.length,
      invalidCount: !_actual?.length ? 0 : invalid.length,
      validCount: valid.length,
    };
  }

  const valid = _actual?.filter((x) => _expected.includes(x)) ?? [];
  const invalid = _actual?.filter((x) => !_expected.includes(x)) ?? [];

  return {
    valid: Boolean(
      _actual?.length === _expected.length &&
        _expected.every((e) => _actual?.includes(e)),
    ),
    invalidCount: invalid.length,
    validCount: valid.length,
  };
}

/**
 * Converts a quiz API response to the format expected by
 * the QuizView UI.
 */
export function toQuizViewQuestions(
  questions: ClassroomQuizQuestion[] = [],
  t: I18nTranslationFunction,
): QuizViewQuestion[] {
  return questions.map<QuizViewQuestion>((q) => {
    return {
      id: q.id,
      title: q.title,
      type: q.type,
      answer: q.answer,
      description: q.description,
      template: (q as FillInTheBlankQuestion).template,
      isCompleted: !!q.user?.completedAt,
      successMessage: {
        // Use a random title as a fallback
        title: t(`quiz.success.title_${getRandomInteger(1, 10)}`),
        ...q.successMessage,
      },
      options: q.options?.map(({ id, content }) => ({
        value: id,
        text: content,
      })),
      items: (q as MatchingQuestion).items?.map(
        ({ id, content }: QuizQuestionItem) => ({
          value: id,
          text: content,
        }),
      ),
    };
  });
}

/**
 * Returns the index value of the first unanswered question.
 * Returns 0 if all questions are answered.
 */
export function getDefaultQuestionIndex(
  questions: Pick<ClassroomQuizQuestion, "user">[] = [],
) {
  return Math.max(0, questions.findIndex((q) => !q.user?.completedAt) ?? 0);
}

/**
 * Serialize answer pairs (objects) into strings. This is
 * required because Quiz question answers are represented as
 * strings.
 */
export function serializeAnswer(i: { key: string; value: string }[]) {
  return i.map((e) => `${e.key}:${e.value}`);
}

/**
 * De-serialize a quiz answer (string) into an object.
 */
export function deserializeAnswer(i: string[]) {
  return i.map<{ key: string; value: string }>((e) => {
    const [key, value] = e.split(":");
    return { key, value };
  });
}
